import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as XLSX from 'xlsx';
import {
    AtokuBusinessMixMonth,
    AtokuBusinessMixPricingProfileMetric,
    AtokuKitchenMonth,
    AtokuKitchenPricingProfileMetric,
    AtokuKitchenReport,
    AtokuKitchenWeek,
    AtokuKitchenYear,
    AtokuPaymentStatementLine,
    AtokuReportPricingProfile,
    AtokuReportsResponse,
    AtokuService,
    AtokuStorageArea,
} from '../services/atoku.service';
import {
    StatementPeriodDialogComponent,
    StatementPeriodDialogResult
} from './dialogs/statement-period-dialog/statement-period-dialog.component';

interface KitchenMetricRow {
    label: string;
    total: number;
    weekValues: number[];
    isHours?: boolean;
}

interface KitchenMonthSection {
    monthLabel: string;
    rows: KitchenMetricRow[];
}

interface KitchenComparisonValue {
    year: number;
    hours: number;
}

interface KitchenComparisonRow {
    monthLabel: string;
    values: KitchenComparisonValue[];
}

interface KitchenSectionView {
    spaceCode: string;
    spaceName: string;
    year: number;
    months: KitchenMonthSection[];
    comparisonYears: number[];
    comparisonRows: KitchenComparisonRow[];
}

@Component({
    selector: 'app-atoku-reports',
    templateUrl: './atoku-reports.component.html',
    styleUrls: ['./atoku-reports.component.scss']
})
export class AtokuReportsComponent implements OnInit {
    readonly weekHeaders = ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5'];
    readonly monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
    ];

    isLoading = false;
    errorMessage = '';
    reports: AtokuReportsResponse | null = null;
    selectedYear: number | null = null;
    businessMixMonths: AtokuBusinessMixMonth[] = [];
    kitchenSections: KitchenSectionView[] = [];
    storageAreas: AtokuStorageArea[] = [];
    isStatementDownloading = false;

    constructor(
        private atokuService: AtokuService,
        private dialog: MatDialog
    ) { }

    ngOnInit(): void {
        this.loadReports();
    }

    get availableYears(): number[] {
        return this.reports?.availableYears ?? [];
    }

    get reportPricingProfiles(): AtokuReportPricingProfile[] {
        return this.reports?.pricingProfiles ?? [];
    }

    get businessMixPricingProfiles(): AtokuReportPricingProfile[] {
        return this.reportPricingProfiles.filter((profile) =>
            this.businessMixMonths.some((month) => this.getBusinessMixMetric(month, profile.code).businesses > 0)
        );
    }

    get generatedOnLabel(): string {
        if (!this.reports?.generatedOn) {
            return '';
        }

        return new Intl.DateTimeFormat('en-ZA', {
            dateStyle: 'medium',
            timeStyle: 'short'
        }).format(new Date(this.reports.generatedOn));
    }

    get selectedYearValue(): number {
        return this.selectedYear ?? new Date().getFullYear();
    }

    get unclassifiedBusinessTotal(): number {
        return this.businessMixMonths.reduce((total, month) => total + Number(month.unclassifiedBusinesses ?? 0), 0);
    }

    onYearChange(year: number): void {
        this.selectedYear = Number(year);
        this.rebuildViewModels();
    }

    exportToExcel(): void {
        if (!this.reports) {
            return;
        }

        const workbook = XLSX.utils.book_new();
        const usedSheetNames = new Set<string>();

        this.appendWorkbookSheet(workbook, this.buildBusinessMixWorksheet(), 'Business Mix', usedSheetNames);

        (this.reports.kitchens ?? []).forEach((report) => {
            this.appendWorkbookSheet(
                workbook,
                this.buildKitchenOccupancyWorksheet(report, this.selectedYearValue),
                `${this.selectedYearValue} ${report.spaceCode}`,
                usedSheetNames
            );
            this.appendWorkbookSheet(
                workbook,
                this.buildKitchenComparisonWorksheet(report),
                `Graph ${report.spaceCode}`,
                usedSheetNames
            );
        });

        this.appendWorkbookSheet(workbook, this.buildStorageWorksheet(), 'Storage', usedSheetNames);

        XLSX.writeFile(workbook, this.buildExportFileName());
    }

    downloadStatement(): void {
        if (this.isStatementDownloading) {
            return;
        }

        const dialogRef = this.dialog.open<StatementPeriodDialogComponent, undefined, StatementPeriodDialogResult>(
            StatementPeriodDialogComponent,
            { width: '520px' }
        );

        dialogRef.afterClosed().subscribe(period => {
            if (!period) {
                return;
            }

            this.isStatementDownloading = true;
            this.atokuService.getPaymentStatement(period.startDate, period.endDate).subscribe({
                next: payments => {
                    this.downloadCsv(this.buildStatementCsv(payments ?? []), this.buildStatementFileName(period));
                    this.isStatementDownloading = false;
                },
                error: () => {
                    this.errorMessage = 'Could not download the Atoku payment statement.';
                    this.isStatementDownloading = false;
                }
            });
        });
    }

    formatPercent(value: number): string {
        return `${(Number(value ?? 0) * 100).toFixed(1)}%`;
    }

    formatMetricValue(value: number, isHours = false): string {
        const normalized = Number(value ?? 0);
        return isHours ? `${normalized} hrs` : `${normalized}`;
    }

    trackByIndex(index: number): number {
        return index;
    }

    trackByProfileCode(index: number, profile: AtokuReportPricingProfile): string {
        return profile.code || `${index}`;
    }

    private loadReports(): void {
        this.isLoading = true;
        this.errorMessage = '';

        this.atokuService.getReports().subscribe({
            next: (reports) => {
                this.reports = reports;
                this.selectedYear = reports.defaultYear
                    || reports.availableYears?.[reports.availableYears.length - 1]
                    || new Date().getFullYear();
                this.rebuildViewModels();
                this.isLoading = false;
            },
            error: () => {
                this.errorMessage = 'Could not load Atoku reports.';
                this.isLoading = false;
            }
        });
    }

    private rebuildViewModels(): void {
        if (!this.reports) {
            this.businessMixMonths = [];
            this.kitchenSections = [];
            this.storageAreas = [];
            return;
        }

        const fallbackYear = this.selectedYear
            ?? this.reports.defaultYear
            ?? this.reports.availableYears?.[0]
            ?? new Date().getFullYear();

        this.selectedYear = fallbackYear;
        this.businessMixMonths = this.reports.businessMixYears.find((year) => year.year === fallbackYear)?.months ?? [];
        this.kitchenSections = (this.reports.kitchens ?? []).map((report) => this.buildKitchenSection(report, fallbackYear));
        this.storageAreas = this.reports.storageAreas ?? [];
    }

    private buildKitchenSection(report: AtokuKitchenReport, selectedYear: number): KitchenSectionView {
        const selectedKitchenYear = report.years.find((year) => year.year === selectedYear)
            ?? report.years[report.years.length - 1];

        return {
            spaceCode: report.spaceCode,
            spaceName: report.spaceName,
            year: selectedKitchenYear?.year ?? selectedYear,
            months: (selectedKitchenYear?.months ?? [])
                .filter((month) => this.hasKitchenMonthData(month))
                .map((month) => this.buildKitchenMonthSection(month)),
            comparisonYears: (report.years ?? []).map((year) => year.year),
            comparisonRows: this.buildKitchenComparisonRows(report)
        };
    }

    private buildKitchenMonthSection(month: AtokuKitchenMonth): KitchenMonthSection {
        const activePricingProfiles = this.getActiveMonthPricingProfiles(month);

        return {
            monthLabel: month.monthLabel.toUpperCase(),
            rows: [
                this.buildMetricRow('Number of bookings', month.totalBookings, month.weeks, (week) => week.bookings),
                this.buildMetricRow('Hours of usage', month.totalHours, month.weeks, (week) => week.hours, true),
                this.buildMetricRow('Number of business', month.totalBusinesses, month.weeks, (week) => week.businesses),
                ...activePricingProfiles.map((profile) => this.buildMetricRow(
                    profile.name,
                    this.getKitchenPricingProfileMetric(month.pricingProfiles, profile.code)?.businesses ?? 0,
                    month.weeks,
                    (week) => this.getKitchenPricingProfileMetric(week.pricingProfiles, profile.code)?.businesses ?? 0
                )),
                ...activePricingProfiles.map((profile) => this.buildMetricRow(
                    `${profile.name} hours`,
                    this.getKitchenPricingProfileMetric(month.pricingProfiles, profile.code)?.hours ?? 0,
                    month.weeks,
                    (week) => this.getKitchenPricingProfileMetric(week.pricingProfiles, profile.code)?.hours ?? 0,
                    true
                ))
            ]
        };
    }

    private buildMetricRow(
        label: string,
        total: number,
        weeks: AtokuKitchenWeek[],
        selector: (week: AtokuKitchenWeek) => number,
        isHours = false
    ): KitchenMetricRow {
        return {
            label,
            total,
            isHours,
            weekValues: Array.from({ length: 5 }, (_, index) => {
                const week = weeks.find((item) => item.week === index + 1);
                return selector(week ?? this.emptyWeek(index + 1));
            })
        };
    }

    private buildKitchenComparisonRows(report: AtokuKitchenReport): KitchenComparisonRow[] {
        return this.monthNames.map((monthLabel, monthIndex) => ({
            monthLabel,
            values: (report.years ?? []).map((year) => ({
                year: year.year,
                hours: year.months.find((month) => month.month === monthIndex + 1)?.totalHours ?? 0
            }))
        })).filter((row) => row.values.some((value) => value.hours > 0));
    }

    private emptyWeek(week: number): AtokuKitchenWeek {
        return {
            week,
            bookings: 0,
            hours: 0,
            businesses: 0,
            pricingProfiles: []
        };
    }

    private hasKitchenMonthData(month: AtokuKitchenMonth): boolean {
        return Number(month.totalBookings ?? 0) > 0
            || Number(month.totalHours ?? 0) > 0
            || Number(month.totalBusinesses ?? 0) > 0;
    }

    private getActiveMonthPricingProfiles(month: AtokuKitchenMonth): AtokuReportPricingProfile[] {
        return this.reportPricingProfiles.filter((profile) => {
            const metric = this.getKitchenPricingProfileMetric(month.pricingProfiles, profile.code);
            return Number(metric?.businesses ?? 0) > 0 || Number(metric?.hours ?? 0) > 0;
        });
    }

    private getKitchenPricingProfileMetric(
        metrics: AtokuKitchenPricingProfileMetric[],
        pricingProfileCode: string
    ): AtokuKitchenPricingProfileMetric | undefined {
        return (metrics ?? []).find((metric) => metric.pricingProfileCode === pricingProfileCode);
    }

    getBusinessMixMetric(
        month: AtokuBusinessMixMonth,
        pricingProfileCode: string
    ): AtokuBusinessMixPricingProfileMetric {
        return (month.pricingProfiles ?? []).find((metric) => metric.pricingProfileCode === pricingProfileCode) ?? {
            pricingProfileCode,
            pricingProfileName: '',
            businesses: 0,
            share: 0
        };
    }

    private buildBusinessMixWorksheet(): XLSX.WorkSheet {
        const pricingProfiles = this.businessMixPricingProfiles.length > 0
            ? this.businessMixPricingProfiles
            : this.reportPricingProfiles;
        const rows: Array<Array<string | number>> = [[
            'Month',
            'Total Bookings',
            ...pricingProfiles.map((profile) => profile.name),
            ...pricingProfiles.map((profile) => `${profile.name} %`)
        ]];

        this.monthNames.forEach((fallbackMonthLabel, monthIndex) => {
            const month = this.businessMixMonths.find((item) => item.month === monthIndex + 1);

            rows.push([
                month?.monthLabel ?? fallbackMonthLabel,
                Number(month?.totalBookings ?? 0),
                ...pricingProfiles.map((profile) => Number(
                    month
                        ? this.getBusinessMixMetric(month, profile.code).businesses
                        : 0
                )),
                ...pricingProfiles.map((profile) => Number(
                    month
                        ? this.getBusinessMixMetric(month, profile.code).share
                        : 0
                ))
            ]);
        });

        const worksheet = XLSX.utils.aoa_to_sheet(rows);
        worksheet['!cols'] = [
            { wch: 18 },
            { wch: 16 },
            ...pricingProfiles.map(() => ({ wch: 18 })),
            ...pricingProfiles.map(() => ({ wch: 14 }))
        ];

        this.applyPercentFormat(worksheet, 1, rows.length - 1, 2 + pricingProfiles.length, pricingProfiles.length);

        return worksheet;
    }

    private buildKitchenOccupancyWorksheet(report: AtokuKitchenReport, selectedYear: number): XLSX.WorkSheet {
        const kitchenYear = this.getSelectedKitchenYear(report, selectedYear);
        const pricingProfiles = this.getActiveKitchenYearPricingProfiles(kitchenYear);
        const rows: Array<Array<string | number>> = [[
            'COMMERCIAL KITCHEN OCCUPANCY',
            '',
            '',
            '',
            '',
            '',
            '',
            ''
        ], [
            'MONTH',
            'WEEK 1',
            'WEEK 2',
            'WEEK 3',
            'WEEK 4',
            'WEEK 5',
            'TOTAL',
            'Comments (events and bookings)'
        ]];

        this.monthNames.forEach((fallbackMonthLabel, monthIndex) => {
            const month = kitchenYear?.months.find((item) => item.month === monthIndex + 1)
                ?? this.emptyKitchenMonth(monthIndex + 1, fallbackMonthLabel);

            rows.push([
                (month.monthLabel || fallbackMonthLabel).toUpperCase(),
                '',
                '',
                '',
                '',
                '',
                '',
                this.normalizeCellText(month.comments)
            ]);
            rows.push([
                'Number of bookings',
                ...this.buildKitchenWeekCells(month.weeks, (week) => Number(week.bookings ?? 0)),
                Number(month.totalBookings ?? 0),
                ''
            ]);
            rows.push([
                'Hours of usage',
                ...this.buildKitchenWeekCells(month.weeks, (week) => this.formatHoursCell(week.hours)),
                this.formatHoursCell(month.totalHours),
                ''
            ]);
            rows.push([
                'Number of business',
                ...this.buildKitchenWeekCells(month.weeks, (week) => Number(week.businesses ?? 0)),
                Number(month.totalBusinesses ?? 0),
                ''
            ]);

            pricingProfiles.forEach((profile) => {
                rows.push([
                    profile.name,
                    ...this.buildKitchenWeekCells(month.weeks, (week) => {
                        const metric = this.getKitchenPricingProfileMetric(week.pricingProfiles, profile.code);
                        return Number(metric?.businesses ?? 0);
                    }),
                    Number(this.getKitchenPricingProfileMetric(month.pricingProfiles, profile.code)?.businesses ?? 0),
                    ''
                ]);
            });

            pricingProfiles.forEach((profile) => {
                rows.push([
                    `${profile.name} hours`,
                    ...this.buildKitchenWeekCells(month.weeks, (week) => {
                        const metric = this.getKitchenPricingProfileMetric(week.pricingProfiles, profile.code);
                        return this.formatHoursCell(metric?.hours ?? 0);
                    }),
                    this.formatHoursCell(this.getKitchenPricingProfileMetric(month.pricingProfiles, profile.code)?.hours ?? 0),
                    ''
                ]);
            });

            rows.push(['', '', '', '', '', '', '', '']);
        });

        const worksheet = XLSX.utils.aoa_to_sheet(rows);
        worksheet['!cols'] = [
            { wch: 24 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 12 },
            { wch: 14 },
            { wch: 36 }
        ];
        worksheet['!merges'] = [{
            s: { r: 0, c: 0 },
            e: { r: 0, c: 7 }
        }];

        return worksheet;
    }

    private buildKitchenComparisonWorksheet(report: AtokuKitchenReport): XLSX.WorkSheet {
        const comparisonYears = (report.years ?? []).map((year) => year.year);
        const rows: Array<Array<string | number>> = [['', ...comparisonYears]];

        this.monthNames.forEach((monthLabel, monthIndex) => {
            rows.push([
                monthLabel,
                ...comparisonYears.map((yearValue) => Number(
                    report.years.find((year) => year.year === yearValue)?.months.find((month) => month.month === monthIndex + 1)?.totalHours ?? 0
                ))
            ]);
        });

        const worksheet = XLSX.utils.aoa_to_sheet(rows);
        worksheet['!cols'] = [
            { wch: 18 },
            ...comparisonYears.map(() => ({ wch: 12 }))
        ];

        return worksheet;
    }

    private buildStorageWorksheet(): XLSX.WorkSheet {
        const rows: Array<Array<string | number>> = [
            ['', '', '', '', ''],
            ['STORAGE AREA', 'TOTAL NUMBER OF SHELVES', 'SHELVES IN USE', 'SHELVES AVAILABLE', 'Comments']
        ];

        this.storageAreas.forEach((area) => {
            rows.push([area.spaceName, '', '', '', '']);

            area.items.forEach((item) => {
                rows.push([
                    item.storageName,
                    Number(item.totalUnits ?? 0),
                    Number(item.inUseUnits ?? 0),
                    Number(item.availableUnits ?? 0),
                    this.normalizeCellText(item.comments)
                ]);
            });

            rows.push(['', '', '', '', '']);
        });

        const worksheet = XLSX.utils.aoa_to_sheet(rows);
        worksheet['!cols'] = [
            { wch: 24 },
            { wch: 24 },
            { wch: 18 },
            { wch: 20 },
            { wch: 30 }
        ];

        return worksheet;
    }

    private buildStatementCsv(payments: AtokuPaymentStatementLine[]): string {
        const rows: Array<Record<string, string | number>> = payments.map((payment) => ({
            Date: this.formatStatementDate(this.pickValue(payment, [
                'paymentDate',
                'payment_date',
                'transactionDate',
                'transaction_date',
                'paidOn',
                'paid_on',
                'date'
            ])),
            Amount: this.formatStatementAmount(this.pickValue(payment, [
                'amount',
                'paymentAmount',
                'payment_amount',
                'amountIncl',
                'amount_incl',
                'total'
            ])),
            Payee: this.pickString(payment, [
                'payee',
                'customerName',
                'customer_name',
                'userName',
                'user_name',
                'companyName',
                'company_name'
            ]),
            Description: this.pickString(payment, [
                'description',
                'bookingTitle',
                'booking_title',
                'title'
            ]),
            Reference: this.pickString(payment, [
                'reference',
                'paymentReference',
                'payment_reference',
                'transactionReference',
                'transaction_reference',
                'paymentId',
                'payment_id',
                'id'
            ]),
            'Transaction Type': this.pickString(payment, [
                'paymentType',
                'payment_type',
                'type',
                'status'
            ])
        }));

        const worksheet = XLSX.utils.json_to_sheet(rows, {
            header: ['Date', 'Amount', 'Payee', 'Description', 'Reference', 'Transaction Type']
        });
        return XLSX.utils.sheet_to_csv(worksheet);
    }

    private downloadCsv(csv: string, filename: string): void {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
    }

    private getSelectedKitchenYear(report: AtokuKitchenReport, selectedYear: number): AtokuKitchenYear | undefined {
        return report.years.find((year) => year.year === selectedYear)
            ?? report.years[report.years.length - 1];
    }

    private getActiveKitchenYearPricingProfiles(kitchenYear?: AtokuKitchenYear): AtokuReportPricingProfile[] {
        return this.reportPricingProfiles.filter((profile) => (kitchenYear?.months ?? []).some((month) => {
            const metric = this.getKitchenPricingProfileMetric(month.pricingProfiles, profile.code);
            return Number(metric?.businesses ?? 0) > 0 || Number(metric?.hours ?? 0) > 0;
        }));
    }

    private buildKitchenWeekCells(
        weeks: AtokuKitchenWeek[],
        selector: (week: AtokuKitchenWeek) => string | number
    ): Array<string | number> {
        return Array.from({ length: 5 }, (_, index) => {
            const week = weeks.find((item) => item.week === index + 1);
            return week ? selector(week) : '';
        });
    }

    private emptyKitchenMonth(month: number, monthLabel: string): AtokuKitchenMonth {
        return {
            month,
            monthLabel,
            totalBookings: 0,
            totalHours: 0,
            totalBusinesses: 0,
            pricingProfiles: [],
            comments: '',
            weeks: []
        };
    }

    private formatHoursCell(value: number): string {
        return `${Number(value ?? 0)} hrs`;
    }

    private normalizeCellText(value: string | null | undefined): string {
        return String(value ?? '').trim();
    }

    private applyPercentFormat(
        worksheet: XLSX.WorkSheet,
        startRow: number,
        endRow: number,
        startColumn: number,
        totalColumns: number
    ): void {
        for (let rowIndex = startRow; rowIndex <= endRow; rowIndex += 1) {
            for (let columnOffset = 0; columnOffset < totalColumns; columnOffset += 1) {
                const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: startColumn + columnOffset });
                if (worksheet[cellAddress]) {
                    worksheet[cellAddress].z = '0.0%';
                }
            }
        }
    }

    private appendWorkbookSheet(
        workbook: XLSX.WorkBook,
        worksheet: XLSX.WorkSheet,
        preferredName: string,
        usedSheetNames: Set<string>
    ): void {
        const sheetName = this.buildUniqueSheetName(preferredName, usedSheetNames);
        usedSheetNames.add(sheetName);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    }

    private buildUniqueSheetName(preferredName: string, usedSheetNames: Set<string>): string {
        const baseName = (preferredName || 'Sheet')
            .replace(/[\\/?*:[\]]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .slice(0, 31) || 'Sheet';

        let candidateName = baseName;
        let duplicateCount = 1;

        while (usedSheetNames.has(candidateName)) {
            const suffix = ` ${duplicateCount}`;
            candidateName = `${baseName.slice(0, 31 - suffix.length).trim()}${suffix}`;
            duplicateCount += 1;
        }

        return candidateName;
    }

    private buildExportFileName(): string {
        return `atoku-reports-${this.selectedYearValue}-${new Date().toISOString().slice(0, 10)}.xlsx`;
    }

    private buildStatementFileName(period: StatementPeriodDialogResult): string {
        return `atoku-payment-statement-${period.startDate}-to-${period.endDate}.csv`;
    }

    private pickString(payment: AtokuPaymentStatementLine, keys: string[]): string {
        const value = this.pickValue(payment, keys);
        return String(value ?? '').trim();
    }

    private pickValue(payment: AtokuPaymentStatementLine, keys: string[]): unknown {
        const raw = payment as Record<string, unknown>;
        for (const key of keys) {
            if (raw[key] !== undefined && raw[key] !== null && raw[key] !== '') {
                return raw[key];
            }
        }
        return '';
    }

    private formatStatementDate(value: unknown): string {
        if (!value) {
            return '';
        }

        const date = new Date(String(value));
        if (Number.isNaN(date.getTime())) {
            return String(value);
        }

        return date.toISOString().slice(0, 10);
    }

    private formatStatementAmount(value: unknown): number {
        const amount = Number(value ?? 0);
        return Number.isFinite(amount) ? Math.round(amount * 100) / 100 : 0;
    }
}
